/**
 * Friendly, human-readable labels for the stable issue codes emitted by the
 * backend (Stage 1 calendar import + Stage 2 course-requests import + Stage 3
 * post-run). Codes stay as the wire contract; labels render in the UI so
 * admins don't have to read dotted enum names.
 *
 * Unknown codes pass through unchanged so a new backend code surfaces visibly
 * instead of getting silently swallowed; the title block shows the raw code
 * as a secondary line in every case for parity with bug-report copy/paste.
 */
const FRIENDLY_LABELS: Record<string, string> = {
  // Fetch-level (Calendar.Fetch.*)
  "Calendar.Fetch.UrlNotHttps": "Calendar URL is not HTTPS",
  "Calendar.Fetch.HostBlocked": "Calendar host is not allowed",
  "Calendar.Fetch.Timeout": "Calendar download timed out",
  "Calendar.Fetch.TooLarge": "Calendar file is too large",
  "Calendar.Fetch.WrongContentType":
    "Calendar URL returned the wrong content type",
  "Calendar.Fetch.HttpError": "Calendar download failed",
  "Calendar.Fetch.TooManyRedirects": "Calendar URL has too many redirects",

  // Parser-level (Calendar.Parse.*)
  "Calendar.Parse.Malformed": "Calendar file is malformed",
  "Calendar.Parse.NoVEvents": "Calendar has no events",
  "Calendar.Parse.NoCourseEvents": "No events with a Course ID",
  "Calendar.Parse.OccurrenceCap": "Recurrence cap hit",
  "Calendar.Parse.UnknownTimezone": "Unknown time zone",

  // Stage 1 (Calendar import)
  "Calendar.Event.DtEndBeforeStart": "Event ends before it starts",
  "Calendar.Event.MissingTeacherEmail": "Missing teacher email",
  "Calendar.Event.EmptyCourseId": "Empty Course ID",
  "Calendar.Override.NoMaster": "Orphan override (no master event)",
  "Calendar.Room.DoubleBooked": "Room double-booked",
  "Calendar.Teacher.DoubleBooked": "Teacher double-booked",
  "Calendar.Event.MalformedTeacherEmail": "Malformed teacher email",
  "Calendar.Event.TeacherNameNotEmail": "Teacher name used instead of email",
  "Calendar.Event.MalformedCapacity": "Malformed capacity",
  "Calendar.Event.NoLocation": "No location",
  "Calendar.Event.NoSubjectArea": "No subject area",
  "Calendar.Event.OrphanSection": "Section without Course ID",
  "Calendar.Event.UnknownDescriptionKey": "Unknown description key",
  "Calendar.Override.DescriptionDiffers":
    "Override description differs from master",
  "Calendar.Event.AllDayWithCourseId": "All-day event has a Course ID",
  "Calendar.Event.LongDuration": "Event spans more than 8 hours",
  "Calendar.Event.RoomNeedsCapacity": "Room needs capacity",

  // Stage 2 (Course-requests import)
  "CourseRequest.File.Empty": "Course requests file is empty",
  "CourseRequest.File.MissingRequiredColumn": "Missing required column",
  "CourseRequest.File.SuspiciousEncoding": "Suspicious file encoding",
  "CourseRequest.File.UnreadableFormat": "Unreadable file format",
  "CourseRequest.Request.UnknownCourseId": "Requested course not on calendar",
  "CourseRequest.CourseIdOnlyOnCalendar": "Calendar course ID never requested",
  // Retired codes, no longer emitted but kept so any stored rows predating the
  // change render a friendly label until the next revalidate purges them,
  // rather than surfacing the raw dotted code:
  //  · CourseIdOnlyInRequests — duplicated the per-course UnknownCourseId errors
  //  · CourseIdCoverageMismatch — the original combined warning, since split
  "CourseRequest.CourseIdOnlyInRequests": "Requested course ID not on calendar",
  "CourseRequest.CourseIdCoverageMismatch": "Calendar / request course ID mismatch",
  "CourseRequest.Row.MissingStudentIdentifier": "Missing student identifier",
  "CourseRequest.Row.MalformedCourseId": "Missing or malformed course ID",
  "CourseRequest.Row.PriorityOutOfRange": "Priority out of range",
  "CourseRequest.Student.DuplicateCourseRequest": "Duplicate course request",
  "CourseRequest.Student.DuplicateRows": "Student ID has multiple spellings",

  // Stage 3 (Post-run)
  "Scheduler.Student.TimeConflict": "Student has a time conflict",
  "Scheduler.Student.NotPlaced": "Student request could not be placed",
  "Scheduler.Student.LinkedCourseMismatch": "Linked course mismatch",
  "Scheduler.Student.UnrequestedAssignment":
    "Assigned a course the student didn't request",
  "Scheduler.Student.NoBreaks": "Student has no breaks in the day",
  "Scheduler.Student.NoAssignments": "Student has no assignments",
  "Scheduler.Student.LongDay": "Student's day is too long",
  "Scheduler.Section.CapacityExceeded": "Section overfilled",
  "Scheduler.Section.LowFill": "Section is under-filled",
  "Scheduler.Section.Empty": "Section has no students",
  "Scheduler.Section.AmbiguousCourseCode":
    "Course code served by multiple sections",
  "Scheduler.Teacher.OverloadedDay": "Teacher's day is overloaded",
  "Scheduler.LinkedCourses.UnknownReference": "Linked course not found",
};

/**
 * Returns a human-readable label for an issue code, falling back to the raw
 * code when no friendly mapping exists. Render the result as the prominent
 * group title; show the raw code as smaller secondary text below.
 */
export function friendlyIssueLabel(code: string): string {
  return FRIENDLY_LABELS[code] ?? code;
}
